import { useEffect, useMemo, useState } from "react";
import {
	AutocompleteInput,
	Create,
	FormTab,
	FileField,
	FileInput,
	SelectInput,
	BooleanInput,
	TabbedForm,
	TextInput,
	TranslatableInputs,
	useCreate,
	useGetList,
	useNotify,
	useRedirect,
	useTranslate,
	RadioButtonGroupInput,
} from "react-admin";
import { CustomTransferList } from "../../customComponents/CustomTransferList";
import { CustomContentTypeArrayInput } from "../../customComponents/CustomContentTypeArrayInput";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { validatePlanData } from "./planValidate";
import {
	setIndexesForData,
	transformToFormData,
} from "../../utils/customFunctions";
import { CustomToolbar } from "../../customComponents/CustomToolbar";
import { getChoices } from "./shared";
import CustomChipsArrayInput from "../../customComponents/CustomChipsArrayInput";

export const PlanCreate = (props) => {
	const [backgroundPlaylistsArray, setBackgroundPlaylistsArray] = useState(
		[]
	);
	const [categoriesArray, setCategoriesArray] = useState([]);
	const [languagesArray, setLanguagesArray] = useState([]);
	const [languageArray, setLanguageData] = useState([]);
	const [create] = useCreate("plans");
	const translate = useTranslate();
	const redirect = useRedirect();
	const notify = useNotify();

	const { data: backgroundPlaylistsData } = useGetList(
		"background-playlists"
	);

	const { data: categoriesData } = useGetList("categories");

	const { data: languagesData } = useGetList("languages");

	const onSuccess = () => {
		notify(translate("plans.create.success"), "info");
		redirect("/plans");
	};

	const onFailure = (error) => {
		notify(error.message, "warning");
	};

	const defaultLocale = useMemo(
		() => userPreferences.defaultTranslationLanguage,
		[]
	);

	useEffect(() => {
		setLanguagesArray(
			Object.values(languagesData).map((language) =>
				language.name.slice(0, 2).toLowerCase()
			)
		);
		setLanguageData(getChoices(languagesData));
	}, [languagesData]);

	useEffect(() => {
		setCategoriesArray(
			Object.values(categoriesData).map(({ id, titles }) => ({
				id,
				name: titles[defaultLocale],
			}))
		);
	}, [categoriesData]);

	useEffect(() => {
		setBackgroundPlaylistsArray(
			Object.values(backgroundPlaylistsData).map(({ id, names }) => ({
				id,
				name: names[defaultLocale],
			}))
		);
	}, [backgroundPlaylistsData]);

	return (
		<Create {...props} title={translate("plans.create.title")}>
			<TabbedForm
				submitOnEnter={false}
				toolbar={<CustomToolbar />}
				save={(data) => {
					const indexedDataToSend = setIndexesForData(data, [
						"modules",
					]);
					const transformed = transformToFormData(indexedDataToSend);
					create(
						{ payload: { data: transformed } },
						{
							onSuccess,
							onFailure,
						}
					);
				}}
				validate={validatePlanData}
				syncWithLocation={false}
			>
				<FormTab label={translate("plans.tabs.basicInfo.label")}>
					<TranslatableInputs
						locales={languagesArray}
						defaultLocale={defaultLocale}
					>
						<TextInput source="names" />
						<TextInput multiline source="descriptions" />
					</TranslatableInputs>
					<AutocompleteInput
						resettable
						source="backgroundPlaylistId"
						choices={backgroundPlaylistsArray}
					/>
					<SelectInput
						source="categoryId"
						choices={categoriesArray}
					/>
					<RadioButtonGroupInput
						source="languageId"
						choices={languageArray}
						optionText={({ name }) =>
							`misc.names.languages.${name}`
						}
						optionValue="id"
					/>
					<BooleanInput defaultValue={false} source="comingSoon" />
				</FormTab>

				<FormTab label={"Manage Images"}>
					<FileInput
						source="maleImage"
						label="Male Illustration"
						accept="image/*"
						placeholder={"Upload Male Programs Illustration"}
					>
						<FileField source="src" title="Illustration" />
					</FileInput>
					<FileInput
						source="femaleImage"
						label="Female Illustration"
						accept="image/*"
						placeholder={"Upload Female Programs Illustration"}
					>
						<FileField source="src" title="Illustration" />
					</FileInput>
					<FileInput
						source="planPageImage"
						label="Programs Page Illustration"
						accept="image/*"
						placeholder={"Upload Programs Page Illustration"}
					>
						<FileField source="src" title="Illustration" />
					</FileInput>
				</FormTab>

				<FormTab
					label={translate("plans.tabs.modulesManagement.label")}
				>
					<CustomTransferList
						source="modules"
						referenceSource="modules"
						referenceResource="modules"
						referenceField="name"
						isReferenceTranslatable={false}
					/>
				</FormTab>
				<FormTab label={translate("plans.tabs.typesManagement.label")}>
					<CustomContentTypeArrayInput />
				</FormTab>
			</TabbedForm>
		</Create>
	);
};
